import React from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  MenuList,
  Popover,
  TextField,
  InputAdornment,
  Button,
  Stack,
  Chip,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  FilterList as FilterListIcon,
  Search as SearchIcon,
  ArrowDropDown as ArrowDropDownIcon,
  GetApp as GetAppIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import styles from "./genCustomTable.module.scss";

/**
 * NOTE ON DATA SHAPE (UNCHANGED)
 * columns: [{ id, label, minWidth, align, width? }]
 * rows: [{ id, rColumns: [any, any, ...] }] // values can be strings, numbers, React nodes, tags, buttons, etc.
 *
 * New (optional) props introduced – all have safe defaults:
 * - enableToolbar?: boolean (default true) – renders a small toolbar with Export and Select toggles
 * - enableSelectionDefault?: boolean (default false) – initial selection mode state
 * - onSelectionChange?: (ids: string[]|number[]) => void – emitted when selection changes
 * - filterValueExtractor?: (cellValue: any, row: any, colIndex: number) => string
 * - exportFileName?: string (default 'table') – base filename for exports
 * - selectionLeftBorderColor?: string – customize selected-row left highlight
 * - pagination: { mode?: 'page' | 'none' } // backward compatible – existing usage supported
 */

const defaultRows = ["10", "20", "30", "50", "100"];
const defaultIntialState = "10";

export const GenCustomTable = ({
  rows = [],
  columns = [],
  actionBtn,
  renderActionButton,
  maxHeight,
  rColumnMinWidth,
  displayRows,
  intialRowState,
  hideRowVisiblity,
  hideHeaderRowVisiblity,
  pagination,
  // --- new optional props (non-breaking) ---
  enableToolbar = true,
  isExportEnabled = false,
  enableSelectionDefault = false,
  onSelectionChange,
  filterValueExtractor,
  exportFileName = "kriha-teams-report",
  selectionLeftBorderColor = "#7C3AED", // violet-ish highlight similar to mocks
  enableSearch = true,
  searchPlaceholder = "Search item...",
}) => {
  // --- global search state ---
  const [globalQuery, setGlobalQuery] = React.useState("");
  const cellToText = React.useCallback(
    (cell, row, colIndex) => {
      if (filterValueExtractor)
        return filterValueExtractor(cell, row, colIndex);
      if (cell == null) return "";
      if (typeof cell === "string" || typeof cell === "number")
        return String(cell);
      // If React element, try common props used for tags/chips; else fallback to text content heuristics
      if (React.isValidElement(cell)) {
        const p = cell.props || {};
        if (p["data-filter"]) return String(p["data-filter"]);
        if (typeof p.label === "string") return p.label;
        if (typeof p.title === "string") return p.title;
        if (typeof p.children === "string") return p.children;
        // last resort: JSON signature
        try {
          return JSON.stringify(p);
        } catch (e) {
          return "[object]";
        }
      }
      try {
        return JSON.stringify(cell);
      } catch (e) {
        return String(cell);
      }
    },
    [filterValueExtractor]
  );
  // Helper: global search match
  const rowMatchesGlobalQuery = React.useCallback(
    (row, query) => {
      if (!query) return true;
      const norm = String(query).toLowerCase();
      // Check id
      if (row.id != null && String(row.id).toLowerCase().includes(norm)) {
        return true;
      }
      // Check all rColumns
      if (Array.isArray(row.rColumns)) {
        for (let i = 0; i < row.rColumns.length; ++i) {
          const txt = cellToText(row.rColumns[i], row, i);
          if (String(txt).toLowerCase().includes(norm)) return true;
        }
      }
      return false;
    },
    [cellToText]
  );
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(
    Number(intialRowState) || Number(defaultIntialState)
  );

  // selection state
  const [selectionMode, setSelectionMode] = React.useState(
    !!enableSelectionDefault
  );
  const [selectedIds, setSelectedIds] = React.useState(new Set());

  // Export menu state
  const [exportAnchor, setExportAnchor] = React.useState(null);

  // Filters state: map of colIndex -> Set of selected textual values
  const [filters, setFilters] = React.useState({});
  const [filterAnchor, setFilterAnchor] = React.useState(null);
  const [activeFilterCol, setActiveFilterCol] = React.useState(null);
  const [filterSearch, setFilterSearch] = React.useState("");
  const openFilter = Boolean(filterAnchor);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  // ---------- helpers ----------
  // Build unique value map for a column (value -> count)
  const buildValueMap = React.useCallback(
    (colIndex) => {
      const map = new Map();
      rows.forEach((row) => {
        const cell = row.rColumns?.[colIndex];
        const key = cellToText(cell, row, colIndex);
        const prev = map.get(key) || 0;
        map.set(key, prev + 1);
      });
      return map;
    },
    [rows, cellToText]
  );

  // Apply global search, then column filters
  const filteredRows = React.useMemo(() => {
    let base = rows;
    if (globalQuery && globalQuery.trim() !== "") {
      base = base.filter((row) => rowMatchesGlobalQuery(row, globalQuery));
    }
    const activeCols = Object.keys(filters).filter(
      (k) => filters[k] && filters[k].size > 0
    );
    if (activeCols.length === 0) return base;
    return base.filter((row) => {
      for (const k of activeCols) {
        const idx = Number(k);
        const cell = row.rColumns?.[idx];
        const txt = cellToText(cell, row, idx);
        if (!filters[k].has(txt)) return false;
      }
      return true;
    });
  }, [rows, filters, cellToText, globalQuery, rowMatchesGlobalQuery]);

  // selection helpers
  const visibleRows = React.useMemo(() => {
    return filteredRows.slice(
      page * rowsPerPage,
      page * rowsPerPage + rowsPerPage
    );
  }, [filteredRows, page, rowsPerPage]);

  const allVisibleSelected = React.useMemo(
    () =>
      visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id)),
    [visibleRows, selectedIds]
  );

  const toggleSelectAllVisible = () => {
    const next = new Set(selectedIds);
    if (allVisibleSelected) {
      visibleRows.forEach((r) => next.delete(r.id));
    } else {
      visibleRows.forEach((r) => next.add(r.id));
    }
    setSelectedIds(next);
    onSelectionChange && onSelectionChange(Array.from(next));
  };

  const toggleSelectOne = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
    onSelectionChange && onSelectionChange(Array.from(next));
  };

  // ---------- filter popover handlers ----------
  const handleOpenFilter = (event, colIndex) => {
    setActiveFilterCol(colIndex);
    setFilterAnchor(event.currentTarget);
    setFilterSearch("");
  };
  const handleCloseFilter = () => {
    setFilterAnchor(null);
    setActiveFilterCol(null);
    setFilterSearch("");
  };

  const commitFilterChange = (value) => {
    setFilters((prev) => {
      const next = { ...prev };
      const set = new Set(next[activeFilterCol] || []);
      if (set.has(value)) set.delete(value);
      else set.add(value);
      next[activeFilterCol] = set;
      return next;
    });
  };

  const clearFilterForActive = () => {
    if (activeFilterCol == null) return;
    setFilters((prev) => {
      const next = { ...prev };
      delete next[activeFilterCol];
      return next;
    });
  };

  const handleRemoveFilterValue = React.useCallback((colIndex, value) => {
    setFilters((prev) => {
      if (!prev[colIndex]) return prev;
      const nextSet = new Set(prev[colIndex]);
      nextSet.delete(value);
      if (nextSet.size === 0) {
        const { [colIndex]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [colIndex]: nextSet };
    });
  }, []);

  const activeFilterChips = React.useMemo(() => {
    return Object.entries(filters).reduce((acc, [key, valueSet]) => {
      if (!valueSet || valueSet.size === 0) return acc;
      const colIndex = Number(key);
      const column = columns[colIndex] || {};
      const columnLabel = column.label || column.id || `Column ${colIndex + 1}`;
      valueSet.forEach((value) => {
        acc.push({
          id: `${key}-${value}`,
          columnIndex: key,
          label: columnLabel,
          value,
        });
      });
      return acc;
    }, []);
  }, [filters, columns]);

  // ---------- export helpers (lazy import to avoid hard deps) ----------
  const isPrimitiveExportValue = (value) => {
    if (value === null || value === undefined) return true;
    const type = typeof value;
    return type === "string" || type === "number" || type === "boolean";
  };

  const getExportableColumnMeta = (dataRows) => {
    const inspectionRows = dataRows && dataRows.length > 0 ? dataRows : rows;
    return columns.reduce((acc, column, index) => {
      const includeColumn = inspectionRows.every((row) => {
        const cell = row?.rColumns?.[index];
        return isPrimitiveExportValue(cell);
      });
      if (includeColumn) acc.push({ column, index });
      return acc;
    }, []);
  };

  const normalizeForExcel = (value) => {
    if (value === null || value === undefined) return "";
    return value;
  };

  const normalizeForPdf = (value) => {
    if (value === null || value === undefined) return "";
    return String(value);
  };

  const exportToExcel = async (dataRows = []) => {
    try {
      const xlsxModule = await import(/* webpackChunkName: "xlsx" */ "xlsx");
      const xlsx = xlsxModule?.default ?? xlsxModule;
      if (!xlsx?.utils) throw new Error("xlsx utils not available");

      const rowsForExport = Array.isArray(dataRows) ? dataRows : [];
      const exportColumns = getExportableColumnMeta(rowsForExport);
      const inspectionRows = rowsForExport.length > 0 ? rowsForExport : rows;
      const includeId = inspectionRows.every((row) =>
        isPrimitiveExportValue(row?.id)
      );

      if (!includeId && exportColumns.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("Excel export skipped: no primitive columns to export.");
        return;
      }

      const columnKeyMap = exportColumns.map(({ column, index }) => ({
        key: column.label || column.id || `col_${index}`,
        index,
      }));

      const headerOrder = [
        ...(includeId ? ["id"] : []),
        ...columnKeyMap.map(({ key }) => key),
      ];

      const flat = rowsForExport.map((row) => {
        const out = {};
        if (includeId) out.id = normalizeForExcel(row?.id);
        columnKeyMap.forEach(({ key, index }) => {
          out[key] = normalizeForExcel(row?.rColumns?.[index]);
        });
        return out;
      });

      const worksheetSeed =
        flat.length > 0
          ? flat
          : [
              headerOrder.reduce((acc, key) => {
                acc[key] = "";
                return acc;
              }, {}),
            ];

      const ws = xlsx.utils.json_to_sheet(worksheetSeed, {
        header: headerOrder,
      });
      const wb = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(wb, ws, "Sheet1");
      xlsx.writeFile(wb, `${exportFileName}.xlsx`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Excel export failed. Ensure 'xlsx' is installed.", err);
    }
  };

  const exportToPDF = async (dataRows = []) => {
    try {
      const jsPdfModule = await import(/* webpackChunkName: "jspdf" */ "jspdf");
      const JsPdfConstructor = jsPdfModule.jsPDF || jsPdfModule.default;
      if (!JsPdfConstructor) throw new Error("jsPDF constructor not found");

      const autoTableModule = await import(
        /* webpackChunkName: "autotable" */ "jspdf-autotable"
      );
      const autoTable = autoTableModule.default || autoTableModule;
      if (typeof autoTable !== "function")
        throw new Error("jspdf-autotable not available");

      const rowsForExport = Array.isArray(dataRows) ? dataRows : [];
      const exportColumns = getExportableColumnMeta(rowsForExport);
      if (exportColumns.length === 0) {
        // eslint-disable-next-line no-console
        console.warn("PDF export skipped: no primitive columns to export.");
        return;
      }

      const doc = new JsPdfConstructor({ orientation: "landscape" });
      const head = [
        exportColumns.map(({ column }) => column.label || column.id || ""),
      ];
      const body = rowsForExport.map((r) =>
        exportColumns.map(({ index }) => normalizeForPdf(r.rColumns?.[index]))
      );
      autoTable(doc, { head, body, styles: { fontSize: 8 } });
      doc.save(`${exportFileName}.pdf`);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        "PDF export failed. Ensure 'jspdf' and 'jspdf-autotable' are installed.",
        err
      );
    }
  };

  const handleExportAllExcel = () => exportToExcel(filteredRows);
  const handleExportSelectedExcel = () => {
    const data = filteredRows.filter((r) => selectedIds.has(r.id));
    exportToExcel(data);
  };
  const handleExportAllPDF = () => exportToPDF(filteredRows);
  const handleExportSelectedPDF = () => {
    const data = filteredRows.filter((r) => selectedIds.has(r.id));
    exportToPDF(data);
  };

  // values for active column (with search & counts)
  const activeValueMap = React.useMemo(() => {
    if (activeFilterCol == null) return new Map();
    const map = buildValueMap(activeFilterCol);
    if (!filterSearch) return map;
    const norm = filterSearch.toLowerCase();
    const filtered = new Map();
    for (const [k, v] of map.entries()) {
      if (String(k).toLowerCase().includes(norm)) filtered.set(k, v);
    }
    return filtered;
  }, [activeFilterCol, buildValueMap, filterSearch]);

  return (
    <Paper
      sx={{ width: "100%", overflow: "hidden" }}
      classes={{ root: styles.container }}
    >
      {/* Toolbar */}
      {enableToolbar && (
        <Stack
          direction="row"
          className={styles.toolbar}
          sx={{ alignItems: "center", justifyContent: "space-between" }}
        >
          <Stack direction="column">
            {enableSearch && (
              <TextField
                size="small"
                className={styles.searchField}
                placeholder={searchPlaceholder}
                value={globalQuery}
                onChange={(e) => setGlobalQuery(e.target.value)}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    endAdornment:
                      globalQuery && globalQuery.length > 0 ? (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => setGlobalQuery("")}
                            edge="end"
                            tabIndex={-1}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                  },
                }}
                sx={{
                  borderRadius: "9999px",
                  minWidth: 260,
                  maxWidth: 380,
                  mb: activeFilterChips.length > 0 ? 0.5 : 0,
                }}
              />
            )}
            {activeFilterChips.length > 0 && (
              <Stack
                direction="row"
                className={styles.chipRow}
                sx={{ alignItems: "center" }}
              >
                {activeFilterChips.map((chip) => (
                  <Chip
                    key={chip.id}
                    label={`${chip.label}: ${chip.value}`}
                    onDelete={() =>
                      handleRemoveFilterValue(chip.columnIndex, chip.value)
                    }
                    size="small"
                  />
                ))}
              </Stack>
            )}
          </Stack>
          {isExportEnabled && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "center", flexShrink: 0 }}
            >
              <Tooltip title={selectionMode ? "Exit Select" : "Select Items"}>
                <Button
                  variant="outlined"
                  // size="small"
                  onClick={() => setSelectionMode((s) => !s)}
                  classes={{outlined: styles.selectButton}}
                >
                  {selectionMode ? "Done" : "Select Items"}
                </Button>
              </Tooltip>
              <Button
                variant="contained"
                // size="small"
                endIcon={
                  <ArrowDropDownIcon classes={{ root: styles.exportIconBtn }} />
                }
                onClick={(e) => setExportAnchor(e.currentTarget)}
                classes={{ root: styles.exportButton }}
              >
                Export
              </Button>
              <Menu
                anchorEl={exportAnchor}
                open={Boolean(exportAnchor)}
                onClose={() => setExportAnchor(null)}
              >
                <MenuItem
                  onClick={() => {
                    setExportAnchor(null);
                    handleExportAllExcel();
                  }}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 8 }} />{" "}
                  Export All (Excel)
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setExportAnchor(null);
                    handleExportSelectedExcel();
                  }}
                  disabled={selectedIds.size === 0}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 8 }} />{" "}
                  Export Selected (Excel)
                </MenuItem>
                <Divider />
                <MenuItem
                  onClick={() => {
                    setExportAnchor(null);
                    handleExportAllPDF();
                  }}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 8 }} />{" "}
                  Export All (PDF)
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setExportAnchor(null);
                    handleExportSelectedPDF();
                  }}
                  disabled={selectedIds.size === 0}
                >
                  <GetAppIcon fontSize="small" style={{ marginRight: 8 }} />{" "}
                  Export Selected (PDF)
                </MenuItem>
              </Menu>
            </Stack>
          )}
        </Stack>
      )}

      <TableContainer sx={{ maxHeight: maxHeight }}>
        <Table stickyHeader aria-label="sticky table">
          <TableHead
            style={{
              visibility: hideHeaderRowVisiblity === true ? "hidden" : "",
            }}
          >
            <TableRow>
              {selectionMode && (
                <TableCell
                  align="center"
                  padding="checkbox"
                  style={{ width: 54 }}
                  classes={{ root: styles.header }}
                >
                  <Checkbox
                    indeterminate={selectedIds.size > 0 && !allVisibleSelected}
                    checked={allVisibleSelected}
                    onChange={toggleSelectAllVisible}
                  />
                </TableCell>
              )}
              {columns.map((item, colIndex) => (
                <TableCell
                  key={item.id ?? colIndex}
                  align={item.align}
                  style={{ minWidth: item.minWidth, width: item.width }}
                  classes={{ root: styles.header }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", justifyContent: "center" }}
                  >
                    <span>{item.label}</span>
                    <Tooltip title="Filter">
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenFilter(e, colIndex)}
                      >
                        <FilterListIcon
                          fontSize="inherit"
                          classes={{ root: styles.filterIcon }}
                        />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              ))}
              {actionBtn === true ? (
                <TableCell align="center" className={styles.actionBtnCol}>
                  Actions
                </TableCell>
              ) : (
                ""
              )}
            </TableRow>
          </TableHead>

          {filteredRows.length === 0 ? (
            <TableBody>
              <TableRow>
                <TableCell
                  colSpan={
                    columns.length +
                    (selectionMode ? 1 : 0) +
                    (actionBtn ? 1 : 0)
                  }
                  style={{ textAlign: "center", minWidth: "100%" }}
                >
                  No Data Available
                </TableCell>
              </TableRow>
            </TableBody>
          ) : (
            <TableBody>
              {visibleRows.map((row) => {
                const isSelected = selectedIds.has(row.id);
                return (
                  <TableRow
                    hover
                    tabIndex={-1}
                    key={row.id}
                    selected={isSelected}
                    style={
                      isSelected
                        ? {
                            borderLeft: `4px solid ${selectionLeftBorderColor}`,
                          }
                        : undefined
                    }
                  >
                    {selectionMode && (
                      <TableCell align="center" padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleSelectOne(row.id)}
                        />
                      </TableCell>
                    )}
                    {row.rColumns.map((rColumn, rIdx) => (
                      <TableCell
                        key={rIdx}
                        align="center"
                        style={{ minWidth: rColumnMinWidth }}
                        classes={{ root: styles.bodyCol }}
                      >
                        {rColumn}
                      </TableCell>
                    ))}
                    {actionBtn === true ? (
                      <TableCell align="center" className={styles.actionBtnCol}>
                        <div className={styles.actionBtnContainer}>
                          {renderActionButton(row)}
                        </div>
                      </TableCell>
                    ) : (
                      ""
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          )}
        </Table>
      </TableContainer>

      {/* Pagination (kept but lightly styled) */}
      {pagination?.mode === "page" ? (
        <></>
      ) : (
        <TablePagination
          rowsPerPageOptions={displayRows || defaultRows}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          style={{ display: hideRowVisiblity === true ? "none" : "" }}
          sx={{
            ".MuiTablePagination-actions button": { borderRadius: 1 },
            ".MuiTablePagination-toolbar": { gap: 1 },
          }}
        />
      )}

      {/* Column Filter Popover */}
      <Popover
        open={openFilter}
        anchorEl={filterAnchor}
        onClose={handleCloseFilter}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Stack sx={{ p: 2, minWidth: 280 }} spacing={1}>
          <TextField
            size="small"
            placeholder="Search item..."
            value={filterSearch}
            onChange={(e) => setFilterSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              },
            }}
          />
          <MenuList
            dense
            sx={{ maxHeight: 260, overflowY: "auto", pr: 1 }}
          >
            {Array.from(activeValueMap.entries()).map(([value, count]) => {
              const isChecked = (filters[activeFilterCol] || new Set()).has(
                value
              );
              return (
                <MenuItem
                  key={String(value)}
                  onClick={() => commitFilterChange(value)}
                >
                  <Checkbox size="small" checked={isChecked} />
                  <span
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {String(value)}
                  </span>
                  <Chip label={count} size="small" />
                </MenuItem>
              );
            })}
          </MenuList>
          <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
            <Button size="small" onClick={clearFilterForActive}>
              Clear
            </Button>
            <Button
              size="small"
              variant="contained"
              onClick={handleCloseFilter}
            >
              OK
            </Button>
          </Stack>
        </Stack>
      </Popover>
    </Paper>
  );
};
